import memoize from 'memoize-one';
import { path, propOr } from '../utils/fp';
import { i18n, locales, defaultViewer } from '../lib/settings';
import { fromSearchToState } from '../utils/router';
import { getBasename } from '../utils';

// What is left of the location in redux. React components read the location
// through hooks/router.js instead; these remain for the three kinds of caller a
// useLocation() hook cannot serve:
//
//   - the derived selectors (selectors/{data,index,metadata,microdata,sdmx,
//     search}.js), which mix the location with the sdmx and data slices and are
//     still read through useSelector;
//   - the non-React callers -- ducks/app.js, index.jsx and the navigation hooks'
//     callbacks -- which reach for the location through store.getState();
//   - the components mounted *above* <HistoryRouter> in index.jsx, namely the
//     theme provider, the i18n provider and helmet. They sit outside the router
//     context, so useLocation() throws for them; they read the store, which is
//     mounted higher still.
//
// Every selector below is still reachable from one of those. Once the derived
// selectors stop reading the location and the router is hoisted above the three
// providers, the `router` slice and its mirror can go and react-router is left
// owning the location outright.
export const getRouter = state => state?.router;

const getLocation = memoize(state => propOr({}, 'location', getRouter(state)));

// This is the most important one to memoize to stop the "new object" waterfall
export const getLocationState = memoize(state => {
  const routerState = getLocation(state)?.state;
  return routerState ?? fromSearchToState(window.location.search);
});

export const getPathname = memoize(state => {
  const rawPath = propOr('/', 'pathname', getLocation(state));
  const cleanPath = rawPath.replace(getBasename(), '');
  const normalizedPath = cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;
  return normalizedPath;
});

export const getTerm = memoize(state => propOr('', 'term', getLocationState(state)));

export const getLocale = memoize(state => propOr(i18n.localeId, 'locale', getLocationState(state)));

export const getFacet = memoize(state => getLocationState(state)?.facet);

export const getConstraints = memoize(state => propOr({}, 'constraints', getLocationState(state)));

export const getDataflow = memoize(state => propOr({}, 'dataflow', getLocationState(state)));

export const getDataquery = memoize(state => propOr('', 'dataquery', getLocationState(state)));

export const getHasAccessibility = memoize(state => getLocationState(state)?.hasAccessibility);

export const getHasDataAvailability = memoize(state =>
  propOr(true, 'hasDataAvailability', getLocationState(state)),
);

export const getViewer = memoize(state => propOr(defaultViewer, 'viewer', getLocationState(state)));

export const getPeriod = memoize(state => getLocationState(state)?.period);

export const getDisplay = memoize(state => propOr('label', 'display', getLocationState(state)));

export const getTimeDimensionOrders = memoize(state => propOr({}, 'time', getLocationState(state)));

export const getMicrodataConstraints = memoize(state =>
  propOr({}, 'microdataConstraints', getLocationState(state)),
);

export const getLastNObservations = memoize(state => getLocationState(state)?.lastNObservations);

export const getLastNMode = memoize(state => getLocationState(state)?.lastNMode);

export const getIsRtl = memoize(state => {
  const localeId = getLocale(state);
  return path([localeId, 'isRtl'], locales);
});
